import { SLACK_COLORS } from '@/lib/slack-styles';
import { MrkdwnRenderer } from './mrkdwn-renderer';
import type { SlackBlock } from '@/lib/slack-styles';

interface SlackBlockRendererProps {
  block: SlackBlock;
}

/**
 * Renders a single Slack Block Kit block
 */
export function SlackBlockRenderer({ block }: SlackBlockRendererProps) {
  switch (block.type) {
    case 'section':
      return <SectionBlock block={block} />;
    case 'context':
      return <ContextBlock block={block} />;
    case 'divider':
      return <DividerBlock />;
    case 'actions':
      return <ActionsBlock block={block} />;
    case 'header':
      return <HeaderBlock block={block} />;
    default:
      return null;
  }
}

/**
 * Section block - main content block
 */
function SectionBlock({ block }: { block: SlackBlock }) {
  if (!block.text) return null;

  return (
    <div
      style={{
        padding: '4px 0',
        color: SLACK_COLORS.text,
      }}
    >
      <MrkdwnRenderer text={block.text.text} />
    </div>
  );
}

/**
 * Context block - smaller text for metadata/sources
 */
function ContextBlock({ block }: { block: SlackBlock }) {
  if (!block.elements) return null;

  return (
    <div
      style={{
        padding: '4px 0',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px',
        alignItems: 'center',
      }}
    >
      {block.elements.map((element, index) => {
        if (element.type === 'mrkdwn' && element.text) {
          return (
            <span
              key={index}
              style={{
                fontSize: '12px',
                color: SLACK_COLORS.textSecondary,
                lineHeight: '1.3',
              }}
            >
              <MrkdwnRenderer text={element.text} />
            </span>
          );
        }
        if (element.type === 'plain_text' && element.text) {
          return (
            <span
              key={index}
              style={{
                fontSize: '12px',
                color: SLACK_COLORS.textSecondary,
                lineHeight: '1.3',
              }}
            >
              {element.text}
            </span>
          );
        }
        if (element.type === 'image' && element.url) {
          return (
            <img
              key={index}
              src={element.url}
              alt={element.alt_text || ''}
              style={{
                width: '16px',
                height: '16px',
                borderRadius: '2px',
              }}
            />
          );
        }
        return null;
      })}
    </div>
  );
}

/**
 * Divider block - horizontal line
 */
function DividerBlock() {
  return (
    <hr
      style={{
        border: 'none',
        borderTop: `1px solid ${SLACK_COLORS.borderLight}`,
        margin: '8px 0',
      }}
    />
  );
}

/**
 * Actions block - buttons (non-interactive preview)
 */
function ActionsBlock({ block }: { block: SlackBlock }) {
  if (!block.elements) return null;

  return (
    <div
      style={{
        padding: '8px 0',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
      }}
    >
      {block.elements.map((element, index) => {
        if (element.type === 'button') {
          const buttonText = typeof element.text === 'string'
            ? element.text
            : (element.text as unknown as { text: string })?.text || 'Button';
          return (
            <button
              key={index}
              disabled
              style={{
                padding: '4px 12px',
                fontSize: '13px',
                fontWeight: 600,
                color: SLACK_COLORS.text,
                backgroundColor: '#fff',
                border: `1px solid ${SLACK_COLORS.border}`,
                borderRadius: '4px',
                cursor: 'default',
                opacity: 0.7,
              }}
            >
              {buttonText}
            </button>
          );
        }
        return null;
      })}
    </div>
  );
}

/**
 * Header block - larger, bold text
 */
function HeaderBlock({ block }: { block: SlackBlock }) {
  if (!block.text) return null;

  return (
    <div
      style={{
        padding: '4px 0',
        fontSize: '18px',
        fontWeight: 700,
        color: SLACK_COLORS.text,
        lineHeight: '1.3',
      }}
    >
      {block.text.text}
    </div>
  );
}

export default SlackBlockRenderer;
